
import {Config} from "../config/Config";
import {BuildingController} from "../controller/BuildingController";
import {EnvironmentManager} from "../environments/EnvironmentManager";
import {Main} from "../Main";
import {ToolsView} from "./ToolsView";
import {Scene as Scene3D, PerspectiveCamera, WebGLRenderer, Object3D, Camera, AmbientLight, DirectionalLight, PCFSoftShadowMap, Material, Vector3, SRGBColorSpace, LinearEncoding, LinearSRGBColorSpace, DisplayP3ColorSpace, LinearToneMapping, ACESFilmicToneMapping, HemisphereLight} from "three";
import {Scene} from "../scene/Scene";
import {Environment} from "../environments/Environment";
import {MaterialManager} from "../material/MaterialManager";
import {WarningWindow} from "../ui/WarningWindow";
import {PhotoGenerator} from "../utils/PhotoGenerator";
import TWEEN from "@tweenjs/tween.js";

/**
 * Container for the 3d view, creates js scene, camera, renderer, lights
 */
export class ContentView
{
	private _config: Config;

	private _width: number;
	private _height: number;

	private _element: HTMLElement;
	private _canvas: HTMLCanvasElement;
	private _scene: Scene;

	private _scene3D: Scene3D;
	private _scene3DOverlay: Scene3D;
	private _camera: PerspectiveCamera;
	private _renderer: WebGLRenderer;
	private _building: Object3D;
	private _environmentScene: Scene3D;

	private _controller: BuildingController;

	private _contextLost: boolean = false;

	private _firstUpdate: boolean = true;

	private _environmentManager: EnvironmentManager;

	private _lights: Object3D;
	private _tools: ToolsView;

	public maxAnisotropy: number;

	public renderStrategy: {
		render(scene1: Scene3D, scene2: Scene3D, camera: Camera);
	};

	constructor(config: Config, scene: Scene, tools: ToolsView)
	{
		this._config = config;
		this._scene = scene;
		this._tools = tools;

		// this can be overriden by plugins (eg. CardboardPlugin)
		this.renderStrategy = this;

		this._element = document.getElementById("content");
		this._canvas = <HTMLCanvasElement>document.getElementById("contentCanvas");
	}

	public async init()
	{
		this.init3D();

		this._environmentManager = new EnvironmentManager(this._scene3D);
		await this._environmentManager.init();

		this._building = new Object3D();
		this._scene.building.addToScene(this._building, {environmentManager: this._environmentManager});

		this._controller = new BuildingController(this, this._tools);

		this._renderer.clear();
		this._renderer.autoClear = false;

		this.maxAnisotropy = this._renderer.capabilities.getMaxAnisotropy();

		window.requestAnimationFrame(this.update);
	}

	private update = () =>
	{
		if (this._contextLost)
		{
			return;
		}

		if (this._controller.update() || this._firstUpdate)
		{
			//this._light.position = new Vector3(0, 100, 200).applyQuaternion(this._camera.quaternion);
			Main.getInstance().render = true;
			this._firstUpdate = false;
		}

		this.renderTick();

		window.requestAnimationFrame(this.update);
	};

	public renderTick()
	{
		TWEEN.update();
		if (Main.getInstance().render)
		{
			this.renderStrategy.render(this._scene3D, this._scene3DOverlay, this._camera);
			Main.getInstance().render = false;
		}
	}

	public render(scene1?: Scene3D, scene2?: Scene3D, camera?: Camera)
	{
		// don't clear color for bg colored dots

		this._renderer.clear(false, true, true);
		this._renderer.render(this._scene3D, this._camera);

		this._renderer.clear(false, true, true);
		this._renderer.render(this._scene3DOverlay, this._camera);

		// debug to render hitScene
		// this._renderer.render(this._nodePlacer.hitScene, this._camera);
	}

	/** this one is for the villagemode */
	public lookAtBuilding(buildingID: number)
	{
		this._controller.lookAtBuilding(buildingID);
	}

	private init3D()
	{
		this._scene3D = new Scene3D();

		// a separate overlay scene is created so that placing objects can always be shown on top
		this._scene3DOverlay = new Scene3D();

		this._camera = new PerspectiveCamera(65, window.innerWidth / window.innerHeight, 1, 10000);

		// lights
		this._lights = new Object3D();

		const light1 = new AmbientLight(0xFFFFFF, 0.2);

		const light2 = new DirectionalLight(0xFFFFFF, 0.8);
		light2.position.set(-2, 5, -0.3);
		light2.position.normalize();

		const light3 = new HemisphereLight(0xffffbb, 0x080820, 1);

		this._lights.add(light1);
		this._lights.add(light2);
		this._lights.add(light3);

		this._scene3DOverlay.add(new AmbientLight(0xffffff));


		let shadowCameraSize = 100;
		const directionalLight = new DirectionalLight(0xFFFFFF, 1);

		directionalLight.position.set(-600, 400, 250).normalize();
		directionalLight.castShadow = true;
		directionalLight.shadow.camera.near = 0.1;
		directionalLight.shadow.camera.far = 1800;
		directionalLight.shadow.camera.right = shadowCameraSize;
		directionalLight.shadow.camera.left = -shadowCameraSize;
		directionalLight.shadow.camera.top = shadowCameraSize;
		directionalLight.shadow.camera.bottom = -shadowCameraSize;
		directionalLight.shadow.mapSize.width = 2048;
		directionalLight.shadow.mapSize.height = 2048;

		if (Main.getInstance().villageMode)
		{
			directionalLight.shadow.camera.left = -shadowCameraSize;
			directionalLight.shadow.camera.top = shadowCameraSize;
			directionalLight.shadow.camera.bottom = -shadowCameraSize * 3;
			directionalLight.shadow.camera.right = shadowCameraSize * 3;
		}

		this._lights.add(directionalLight);

		const directionalLight2 = new DirectionalLight(0xFFFFFF, 0.2);
		directionalLight2.position.set(600, 400, -250).normalize();
		this._lights.add(directionalLight2);

		const directionalLight3 = new DirectionalLight(0xFFFFFF, 1);

		directionalLight3.position.set(-600, 400, 250).normalize();
		directionalLight3.castShadow = true;
		directionalLight3.shadow.camera.near = 0.1;
		directionalLight3.shadow.camera.far = 1800;
		directionalLight3.shadow.camera.right = shadowCameraSize;
		directionalLight3.shadow.camera.left = -shadowCameraSize;
		directionalLight3.shadow.camera.top = shadowCameraSize;
		directionalLight3.shadow.camera.bottom = -shadowCameraSize;
		directionalLight3.shadow.mapSize.width = 2048;
		directionalLight3.shadow.mapSize.height = 2048;

		if (Main.getInstance().villageMode)
		{
			directionalLight3.shadow.camera.left = -shadowCameraSize;
			directionalLight3.shadow.camera.top = shadowCameraSize;
			directionalLight3.shadow.camera.bottom = -shadowCameraSize * 3;
			directionalLight3.shadow.camera.right = shadowCameraSize * 3;
		}

		this._lights.add(directionalLight3);

		const directionalLight4 = new DirectionalLight(0xFFFFFF, 1);

		directionalLight4.position.set(-600, 400, 250).normalize();
		directionalLight4.castShadow = true;
		directionalLight4.shadow.camera.near = 0.1;
		directionalLight4.shadow.camera.far = 1800;
		directionalLight4.shadow.camera.right = shadowCameraSize;
		directionalLight4.shadow.camera.left = -shadowCameraSize;
		directionalLight4.shadow.camera.top = shadowCameraSize;
		directionalLight4.shadow.camera.bottom = -shadowCameraSize;
		directionalLight4.shadow.mapSize.width = 2048;
		directionalLight4.shadow.mapSize.height = 2048;

		if (Main.getInstance().villageMode)
		{
			directionalLight4.shadow.camera.left = -shadowCameraSize;
			directionalLight4.shadow.camera.top = shadowCameraSize;
			directionalLight4.shadow.camera.bottom = -shadowCameraSize * 3;
			directionalLight4.shadow.camera.right = shadowCameraSize * 3;
		}

		this._lights.add(directionalLight4);

		// renderer

		const context = this._canvas.getContext('webgl2') || this._canvas.getContext('experimental-webgl2')

		this._renderer = new WebGLRenderer({
			antialias: true,
			canvas: this._canvas,
			context: <WebGLRenderingContext>context,
		});

		//this._renderer.useLegacyLights = true;
		//this._renderer.toneMapping = ACESFilmicToneMapping;

		this._renderer.setPixelRatio(window.devicePixelRatio);

		this._renderer.shadowMap.enabled = false;// Main.getInstance().shadowsToggler.checked;
		this._renderer.shadowMap.type = PCFSoftShadowMap;

		this._renderer.setClearColor(0xcccccc, 1);

		this._renderer.domElement.addEventListener("webglcontextlost", this.onContextLost);

		this._scene3D.add(this._lights);
	}

	private onContextLost = async (event: Event) =>
	{
		event.preventDefault();
		this._contextLost = true;

		await WarningWindow.open(`An error has occurred. Please reload the page to continue.`);
	};

	public resize(width: number, height: number)
	{
		this._width = width;
		this._height = height;

		this._camera.aspect = width / height;
		this._camera.updateProjectionMatrix();

		this._renderer.setSize(width, height, false);

		Main.getInstance().render = true;
	}

	public getScreenShot(width: number, height: number): string
	{
		const photoGenerator = new PhotoGenerator(this._scene3D, width, height);
		photoGenerator.setCameraPosition(this._camera.position);
		photoGenerator.setCameraTarget(new Vector3(0, 0, 0));

		const dataURL = photoGenerator.getPhoto();

		return dataURL;
	}

	public toggleShadows = (value: boolean) =>
	{
		this._renderer.shadowMap.enabled = value;
		this._renderer.shadowMap.autoUpdate = value;

		(<Material>Environment.ground.material).needsUpdate = true;
		Main.getInstance().scene.building.concreteSlab.needsUpdate();
		Main.getInstance().render = true;
	};

	public toggleEnvMaps(value: boolean)
	{
		Main.getInstance().scene.building.supportBeam.toggleEnvMap(value);
		MaterialManager.getInstance().toggleEnvMap(value);

		Main.getInstance().render = true;
	}

	public get canvas()
	{
		return this._canvas;
	}

	public get camera(): PerspectiveCamera
	{
		return this._camera;
	}

	public get scene()
	{
		return this._scene;
	}

	public get scene3D(): Scene3D
	{
		return this._scene3D;
	}

	public get renderer(): WebGLRenderer
	{
		return this._renderer;
	}

	public get scene3DOverlay(): Scene3D
	{
		return this._scene3DOverlay;
	}

	public get controller()
	{
		return this._controller;
	}

	public get config()
	{
		return this._config;
	}

	public get building()
	{
		return this._building;
	}

	public get environment(): Scene3D
	{
		return this._environmentScene;
	}

	public get environmentManager(): EnvironmentManager
	{
		return this._environmentManager;
	}
}
